// ════════════════════════════════════════════════════════════════════════════
// Padrón temporal de comedor — lógica pura del frontend (sin pantalla ni red).
// El backend es quien clasifica y valida (vista previa y, de nuevo, al confirmar). Aquí sólo se lee el archivo tal
// cual (sin transformar IDs), se arma la lista para la captura y se dan los textos de cada estado.
// area_comedor es SÓLO el destino de entrega (NAVE 1 o NAVE 2), no la sucursal laboral.
// ════════════════════════════════════════════════════════════════════════════

export const COLUMNAS_ARCHIVO_PADRON = ['ID', 'NOMBRE_COMPLETO', 'AREA'];
export const AREAS_COMEDOR = ['NAVE 1', 'NAVE 2'];

// ID oficial tal como debe capturarse: texto de exactamente 5 dígitos y distinto de 00000 (misma regla que el backend).
export const idPadronValido = v => typeof v === 'string' && /^\d{5}$/.test(v) && v !== '00000';
export const nombrePersona = v => String(v === undefined || v === null ? '' : v).replace(/\s+/g, ' ').trim();

// Lee la primera hoja del archivo. Exige los encabezados ID, NOMBRE_COMPLETO y AREA (en cualquier orden). Los valores
// se envían COMO VIENEN: un ID guardado como número sigue siendo número (el backend lo marca incompatible) y uno de
// texto no se toca. Las filas vacías se omiten; `fila` es el renglón de Excel y sólo sirve para mostrar.
export function leerArchivoPadron(bytes, XLSX) {
  let libro;
  try { libro = XLSX.read(bytes, { type: 'array' }); } catch (e) { return { error: 'No se pudo leer el archivo: ' + e.message }; }
  if (!libro.SheetNames.length) return { error: 'El archivo no tiene hojas' };
  const hoja = libro.Sheets[libro.SheetNames[0]];
  const filas = XLSX.utils.sheet_to_json(hoja, { header: 1, raw: true, defval: null, blankrows: true });
  const enc = (filas[0] || []).map(h => String(h === null ? '' : h).trim());
  const col = Object.fromEntries(COLUMNAS_ARCHIVO_PADRON.map(c => [c, enc.indexOf(c)]));
  const faltan = COLUMNAS_ARCHIVO_PADRON.filter(c => col[c] === -1);
  if (faltan.length) return { error: 'Faltan columnas en el encabezado: ' + faltan.join(', ') + ' (se esperan ' + COLUMNAS_ARCHIVO_PADRON.join(', ') + ')' };
  const registros = [];
  filas.slice(1).forEach((f, i) => {
    if (!f || f.every(v => v === null || String(v).trim() === '')) return;
    registros.push({ fila: i + 2, id_empleado: f[col.ID], nombre_completo: f[col.NOMBRE_COMPLETO], area_comedor: f[col.AREA] });
  });
  if (!registros.length) return { error: 'El archivo no tiene registros' };
  return { hoja: libro.SheetNames[0], otrasHojas: libro.SheetNames.slice(1), registros };
}

export const ETIQUETA_ESTADO_IMPORTACION = {
  NUEVO: 'Nuevo', SIN_CAMBIOS: 'Ya existe, sin cambios', COMPLETA_AREA: 'Ya existe: se completa su destino',
  CONFLICTO_NOMBRE: 'Conflicto de nombre (no se sobrescribe)', ID_VACIO: 'ID vacío', ID_INCOMPATIBLE: 'ID incompatible',
  ID_DUPLICADO_EN_ARCHIVO: 'ID duplicado en el archivo', ID_REPETIDO_EN_PADRON: 'ID repetido en el padrón', NOMBRE_VACIO: 'Nombre vacío',
  AREA_FALTANTE: 'Destino faltante', AREA_INVALIDA: 'Destino inválido (NAVE 1 o NAVE 2)'
};
export const ESTADOS_QUE_SE_GUARDAN = ['NUEVO', 'COMPLETA_AREA'];
export const ESTADOS_SIN_PROBLEMA = ['NUEVO', 'SIN_CAMBIOS', 'COMPLETA_AREA'];

// Activos de EMPLEADOS que no vienen en el archivo ni están ya en el padrón: sólo aviso (no se agregan solos).
export function activosAusentes(empleadosActivos, registros, personas) {
  const presentes = new Set([...(registros || []).map(r => r.id_empleado), ...(personas || []).map(p => p.id_empleado)].filter(v => typeof v === 'string'));
  return (empleadosActivos || []).map(e => String(e.id)).filter(id => !presentes.has(id)).sort();
}

// Personas seleccionables en la captura cuando la fuente es el padrón: habilitadas. Forma común con la de EMPLEADOS.
export const personasCaptura = personas => (personas || []).filter(p => p.habilitado)
  .map(p => ({ id: p.id_empleado, nombre: p.nombre_completo, destino: p.area_comedor }));

export const MENSAJE_NO_AUTORIZADO = 'No autorizado: tu sesión venció o tu cuenta no está autorizada para el padrón. Vuelve a iniciar sesión; lo capturado se conserva.';
